import {IPlayerService} from '../../interfaces/IPlayerService';
import {Observable, of, shareReplay, switchMap, tap} from "rxjs";
import {delay, repeat} from "rxjs/operators";
import {Log, LogTag} from "../Log";
import {ILogInfo} from "../../interfaces/ILogInfo";

export class ScheduleEvents {
  playerService: IPlayerService;
  scheduleId;
  eventName;
  schedule;
  buffer = [];
  today;
  curPoint;
  nextPoint;
  channels;
  curPriority;    // 0-once 1-yearly 2-monthly 3-weekly 4-daily
  fireItem;
  utcOffset;
  initialized = false;
  sHour = 60 * 60;
  sDAY = 24 * 60 * 60;


  constructor(playerService: IPlayerService, scheduleId) {
    this.playerService = playerService;
    this.scheduleId = scheduleId;
    //

    const now = new Date();
    this.utcOffset = now.getTimezoneOffset() * 60;
    const time = now.getTime() / 1000 - this.utcOffset;
    this.today = Math.floor( time /  this.sDAY );
    this.today--;


  }

  load(): Observable<any> {
    return this.playerService.caching.getRecord("schedules", this.scheduleId).pipe(
      tap( res => {
        this.schedule = res.schedules;
        this.eventName = "event_schedule_" + this.scheduleId;
      }),
      shareReplay(1)
    )
  }

  play(): Observable<any> {
    return of("schedule").pipe(
      switchMap(r => this.playNext()),
      repeat()
    ).pipe(
      shareReplay(1)
    )
  }

  removeItem(channel, item) {
    for(let i=0; i<channel.length;i++) {
      if (channel[i]===item) {
        channel.splice(i, 1);
        break;
      }
    }
  }



  playNext(): Observable<any> {
    this.curPoint = this.nextPoint;
    if (this.curPoint!=null) {
      let channel = this.channels[this.curPoint.item.priority];
      if (this.curPoint.state == 1) {
        channel.push(this.curPoint.item);
        if (this.curPoint.item.priority <= this.curPriority) {
          this.curPriority = this.curPoint.item.priority;
        }
      } else { //state==0
        this.removeItem(channel, this.curPoint.item);
        channel = null;
        while(this.curPriority<5) {
          channel = this.channels[this.curPriority];
          if (channel.length>0) {
            break;
          }
          this.curPriority++;
        }
      }
    }


    let t1 = new Date().getTime() / 1000 - this.utcOffset;
    let t2;
    if (this.buffer.length==0) {
      this.today++;
      t2 = this.today * this.sDAY;
      this.buildBuffer();
    } else {
      this.nextPoint = this.buffer.shift();
      t2 = this.nextPoint.time;
    }


    const duration = t2 - t1;

    if (this.initialized==false && duration>=0) {
      this.initialized = true;
    }


    if (this.initialized) {
      const channel = this.channels[this.curPriority];
      if (channel.length>0) {
        const item = channel[channel.length-1];
        if (this.fireItem != item) {
          this.fireItem = item;
          //Log.d(this, LogTag.SCHEDULE, 'Schedule: ' + new Date().toLocaleTimeString() +  ' event=' + this.fireItem.event_value);
          let param = ""
          try {
            param = JSON.parse(this.fireItem.event_value);
          } catch (e) {
            param = this.fireItem.event_value;
          }
          this.playerService.eventParams.dispatch(this.eventName, param, false);
        }
      } else {
        this.playerService.eventParams.dispatch(this.eventName, {}, false);
      }
    }


    let timeout = Math.max(1, duration * 1000);

    //??? timeout = 5000; // TEST ???


    return of("next").pipe(
      delay(timeout)
    )
  }

  buildBuffer():void {
    const date = new Date((this.today * this.sDAY + this.utcOffset) * 1000);
    const tm = date.getTime();
    const h = date.getHours();
    const day = Math.pow(2, date.getDay());
    const mt = date.getMonth();
    this.buffer = [];
    this.schedule.forEach( item => {
      const t1 = new Date(item.start_date).getTime() / 1000 - this.utcOffset;
      const d1 = Math.floor(t1 / this.sDAY);
      const matchStartDate = (this.today>=d1);
      const t2 = new Date(item.end_date).getTime() / 1000 - this.utcOffset;
      const d2 = Math.floor(t2 / this.sDAY);
      const matchEndDate = item.repeat_forever || (this.today<=d2);
      let exist: boolean = false;
      item.priority = item.repeat_type;
      switch(item.repeat_type) {
        case 0: // once
          exist = this.today==d1;
          break;
        case 1: { // yearly
          const dt = date.getDate();
          exist = matchStartDate && matchEndDate && (item.yearly_offset == mt) && (item.monthly_offset == dt);
          break;
        }
        case 2: { // monthly
          const mt1 = date.getMonth();
          if (item.monthly_offset==28) { // end of month
            const tomorrow = new Date(date.getTime() + this.sDAY * 1000);
            const mt2 = tomorrow.getMonth();
            exist = matchStartDate && matchEndDate && (mt1!=mt2);
          } else { //1-27
            const dt = date.getDate();
            exist = matchStartDate && matchEndDate && (item.monthly_offset == dt);
          }

          break;
        }
        case 3: // weekly
          exist = matchStartDate && matchEndDate && ((day & item.weekly_days) != 0);
          break;
        case 4: // hourly
          exist = matchStartDate && matchEndDate;
          break;
        case 5: // daily
          exist = matchStartDate && matchEndDate;
          break;
      }


      if (exist) {
        if (item.repeat_type==4) { // hourly
         for(let h=0; h<24; h++) {
           const point1: any = {};
           point1.time = this.today * this.sDAY + h * this.sHour + +item.hourly_offset;
           point1.state = 1;
           point1.item = item;
           this.buffer.push(point1);

           const point2: any = {};
           point2.time = point1.time + +item.duration;
           point2.state = 0;
           point2.item = item;
           this.buffer.push(point2);
         }
        } else {
          const point1: any = {};
          point1.time = this.today * this.sDAY + +item.time;
          point1.state = 1;
          point1.item = item;
          this.buffer.push(point1);

          const point2: any = {};
          point2.time = point1.time + +item.duration;
          point2.state = 0;
          point2.item = item;
          this.buffer.push(point2);
        }
      }
    });

    this.buffer.sort( (a,b) => {
      // if points has the same time then pick the point with state==1 as the next one.
      return (a.time - a.state) - (b.time - b.state);
    });

    this.curPoint = null;
    this.nextPoint = null;
    this.channels = [[], [], [], [], [], []];
    this.curPriority = 5;
    this.fireItem = null;
  }

  destroy(): void {
  }
}
